import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import axios from '../api/axios';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';
import WeChatGroupFloat from '../components/WeChatGroupFloat';
import PaymentConfirmDialog from '../components/PaymentConfirmDialog';
import { Loader2, CheckCircle2, ExternalLink } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import UnifiedConfirmDialog from '../components/UnifiedConfirmDialog';

// Types

interface RefillItem {
  name: string;
  quantity: number;
  unit_amount: number;
}

export interface RefillHistoryItem {
  id: number;
  date: string; // YYYY-MM-DD
  medicine_names: string; // comma separated
  medicines?: string;     // legacy
  quantity: number;
  amount: number;
  status: string; // pending, unpaid, paid, shipped, etc.
  shipping_status: string; // shipped, none, etc.
  tracking_number?: string;
  service?: string; // 'aga', 'diet', 'pill'
  refill_items?: RefillItem[];
  receipt_url?: string;
  coupon_code?: string;
  coupon_discount?: number;
  payment_status?: string; // Added
  shipping_carrier?: string;
}

const MypageExtra: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [history, setHistory] = useState<RefillHistoryItem[]>([]);

  const btnLongBase = `
    w-full flex items-center justify-center gap-2
    px-6 py-3 rounded-xl text-[14px] font-bold
    transition-all duration-200 shadow-sm active:scale-[0.98]
  `;
  const btnOrange = 'bg-orange-500 text-white hover:bg-orange-600 shadow-orange-100';
  const btnIndigo = 'bg-orange-500 text-white hover:bg-orange-600 shadow-orange-100';
  const btnEmerald = 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-emerald-100';
  const btnDisabled = 'bg-orange-500/30 text-white cursor-not-allowed shadow-none';

  const [activeTab, setActiveTab] = useState<'applications' | 'purchases'>('applications');

  // Dialogs
  const [showPaymentDlg, setShowPaymentDlg] = useState(false);
  const [paymentReservation, setPaymentReservation] = useState<RefillHistoryItem | null>(null);
  const [confirmingRid, setConfirmingRid] = useState<number | null>(null); // 決済確認中の rid(二重決済防止で「支払う」を無効化)

  const [confirmConfig, setConfirmConfig] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => { } });

  // 1. Sync URL params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'purchases' || tabParam === 'history') { // 'history' mapped to 'purchases' for backward compat or user preference
      setActiveTab('purchases');
    } else if (tabParam === 'applications' || tabParam === 'request') {
      setActiveTab('applications');
    }
  }, [location.search]);

  // 1.5 追加配送の決済完了で戻ってきた場合(?pay=success): 決済確定を待って購入履歴へ。
  //     非同期決済(Alipay/WeChat)や webhook 遅延で redirect 時点では未確定のことがあるため、
  //     /pay/confirm(保険)→ 反映確認 →(未確定なら)/pay/status を短時間ポーリングする。
  //     最後まで未確定なら申請履歴に留める(空の購入履歴を見せない)。
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('pay') !== 'success') return;
    const sessionId = params.get('session_id') || '';
    const ridStr = params.get('rid');
    const rid = ridStr ? Number(ridStr) : undefined;
    let cancelled = false;
    // 決済直後はこの単を再度「支払う」できないよう無効化(非同期決済 processing 中の二重決済を軽減)
    setConfirmingRid(rid ?? null);

    // この rid が「支払済み(購入履歴側)」に反映されたか
    const isPaid = (list: RefillHistoryItem[]) =>
      !!rid && list.some(h => h.id === rid && (h.status === 'paid' || h.status === 'shipped'));

    (async () => {
      // 1) session_id で確定を試みる(webhook の保険)。非同期決済の processing 時は 402 → webhook/ポーリングに委ねる。
      if (sessionId) {
        try { await axios.post('/pay/confirm', { session_id: sessionId }); } catch { /* noop */ }
      }
      if (cancelled) return;

      // 2) 反映確認。未確定なら /pay/status を最大 ~20s ポーリング。
      let list = await fetchHistory();
      if (rid && !isPaid(list)) {
        const start = Date.now();
        while (!cancelled && Date.now() - start < 20000) {
          await new Promise(r => setTimeout(r, 1500));
          if (cancelled) return;
          try {
            const s = await axios.get('/pay/status', { params: { reservation_id: rid } });
            if (s.data?.payment_status === 'paid') break;
          } catch { /* /pay/status 落ちは無視して次周回 */ }
        }
        if (cancelled) return;
        list = await fetchHistory();
      }

      if (cancelled) return;
      setConfirmingRid(null); // 確認フロー終了 → ボタン再有効化
      // 確定していれば購入履歴、まだなら申請履歴(未払いのまま見せる)。
      setActiveTab(isPaid(list) ? 'purchases' : 'applications');
      // URL クエリを消して F5 での再確定を防ぐ
      navigate(location.pathname, { replace: true });
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  // 3. Fetch History(取得したリストも返す: 決済反映のポーリング判定に使う)
  const fetchHistory = async (): Promise<RefillHistoryItem[]> => {
    setLoading(true);
    try {
      const res = await axios.get<RefillHistoryItem[]>('/refills/history');
      const data = res.data || [];
      setHistory(data);
      return data;
    } catch (err) {
      console.error('Error fetching history:', err);
      return [];
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []); // Run once on mount (and re-run if we manually call it generally)

  const handleLogout = () => {
    // Implement logout logic or call external logout function
    window.location.href = '/login';
  };

  // Derived Lists
  const applications = history.filter(h =>
    h.status === 'pending' ||
    h.status === 'unpaid' ||
    // Include 'completed' but 'unpaid' items to allow payment in Applications tab
    (h.status === 'completed' && (!h.payment_status || h.payment_status === 'unpaid'))
  );

  const purchases = history.filter(h =>
    // Include only paid items from completed set
    (h.status === 'completed' && h.payment_status === 'paid') ||
    h.status === 'paid' ||
    h.status === 'shipped' ||
    h.status === 'canceled' ||
    h.status === 'cancelled' ||
    h.status === 'user_cancelled' ||
    h.status === 'admin_cancelled' ||
    h.status === 'doctor_cancelled' ||
    h.status === 'expired'
  );

  const [expandedItems, setExpandedItems] = useState<Set<number>>(new Set());

  const toggleExpand = (id: number) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const renderHistoryList = (list: RefillHistoryItem[], emptyMsg: string) => {
    if (list.length === 0) {
      return (
        <div className="text-center py-12 text-gray-500">
          {emptyMsg}
        </div>
      );
    }
    return (
      <div className="space-y-4">
        {list.map((item) => {
          const isPurchased = activeTab === 'purchases';
          const isExpanded = expandedItems.has(item.id);

          return (
            <div key={item.id} className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 relative transition-all duration-200">
              {/* Header / Summary */}
              <div
                className={`flex flex-col gap-3 ${isPurchased ? 'cursor-pointer' : ''}`}
                onClick={() => isPurchased && toggleExpand(item.id)}
              >
                {/* Uppermost Row: Title and Amount */}
                <div className="flex justify-between items-start">
                  <div className="font-bold text-gray-800 text-base">
                    {t('mypage.extra_delivery') || '追加配送'} ID: {item.id}
                    <span className="ml-2 font-normal text-gray-600 text-sm">
                      {item.service ? t(`service.${item.service}`) : (t('common.service_unknown') || '不明なサービス')}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-lg font-bold text-gray-900">{item.amount?.toLocaleString() || 0}</span>
                    <span className="ml-0.5 text-xs text-gray-500">円</span>
                  </div>
                </div>

                {/* Second Row: Badges, Date, and Toggle Icon */}
                <div className="flex justify-between items-end">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {['paid', 'completed'].includes(item.status) ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 font-semibold text-sm">
                          <CheckCircle2 className="w-4 h-4" />
                          {item.shipping_status === 'shipped'
                            ? (t('common.shipped') || '発送済み')
                            : (t('common.paid') || '支払い済み')}
                        </span>
                      ) : (
                        <span
                          className={`px-3 py-1 rounded-full text-[11px] font-bold border whitespace-nowrap ${['canceled', 'cancelled', 'user_cancelled', 'admin_cancelled', 'expired'].includes(item.status)
                            ? 'bg-red-50 text-red-700 border-red-100'
                            : 'bg-emerald-50 text-emerald-700 border-emerald-100'
                            }`}
                        >
                          {t(`status.${item.status}`) || item.status}
                        </span>
                      )}

                      {/* Shipping status logic combined above or handled here if needed additionally */}
                      {item.shipping_status && item.shipping_status !== 'none' && !['paid', 'completed'].includes(item.status) && (
                        <span className="px-3 py-1 rounded-full text-[11px] font-bold bg-orange-50 text-orange-600 border border-orange-100">
                          {t('common.shipped') || item.shipping_status}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 font-mono">
                      {item.date}
                    </div>
                  </div>

                  {isPurchased && (
                    <div className="text-xs text-gray-400">
                      {isExpanded ? `▲ ${t('common.close') || '閉じる'}` : `▼ ${t('mypage.more') || '詳細'}`}
                    </div>
                  )}
                </div>

                {/* Buttons outside collapsible area (Always visible if Purchased) */}
                {isPurchased && (
                  <div className="flex flex-wrap gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                    {item.receipt_url && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          // 領収書は自社バックエンド(S3→Stripe領収書→自己生成)から配信。Stripe公開ページへは飛ばさない
                          // ポップアップブロック回避: クリック直後に空タブを開き、取得後に遷移させる。
                          const win = window.open('', '_blank');
                          try {
                            const res = await axios.get(`/receipts/pdf/${item.id}`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            if (win) { win.location.href = url; } else { window.open(url, '_blank'); }
                          } catch {
                            if (win) win.close();
                            alert(t('payment.receipt_open_failed') || '領収書を開くことができませんでした');
                          }
                        }}
                        className="inline-flex items-center gap-1 text-xs font-semibold text-coral bg-coral/5 hover:bg-coral-light border border-coral/20 px-2 py-0.5 rounded-full transition cursor-pointer"
                      >
                        {t('common.receipt') || '領収書'}
                        <ExternalLink className="w-3 h-3" />
                      </button>
                    )}
                    {item.tracking_number && (
                      <a
                        href={(() => {
                          // コピペ由来のハイフン/空白を除去（照会URLは素の番号前提）。carrier は yupack を正とし旧 yu_pack も許容
                          const n = (item.tracking_number || '').replace(/[^0-9A-Za-z]/g, '');
                          const c = item.shipping_carrier;
                          return c === 'sagawa'
                            ? `https://k2k.sagawa-exp.co.jp/p/web/okurijosearch.do?okurijoNo=${n}`
                            : (c === 'yupack' || c === 'yu_pack')
                              ? `https://trackings.post.japanpost.jp/services/srv/search/direct?searchKind=S004&locale=ja&reqCodeNo1=${n}`
                              : `https://toi.kuronekoyamato.co.jp/cgi-bin/tneko?number01=${n}`;
                        })()}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 px-2 py-0.5 rounded-full transition"
                      >
                        {t('common.tracking_number') || '配送状況'}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}
              </div>

              {/* Detailed Content (Collapsible) */}
              {(!isPurchased || isExpanded) && (
                <div className={`mt-4 pt-4 border-t border-gray-100 text-sm ${isPurchased ? 'animate-in fade-in slide-in-from-top-1 duration-200' : ''}`}>

                  {/* Items List */}
                  {item.refill_items && item.refill_items.length > 0 ? (
                    <ul className="space-y-1 bg-gray-50 p-3 rounded-lg mb-3">
                      {item.refill_items.map((ri, idx) => (
                        <li key={idx} className="flex justify-between text-gray-600">
                          <span>{ri.name}</span>
                          <span className="font-mono">x {ri.quantity}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-gray-600 mb-2">{item.medicine_names || item.medicines}</p>
                  )}

                  {/* Coupon Info */}
                  {item.coupon_discount && item.coupon_discount > 0 ? (
                    <div className="flex justify-between items-center text-red-500 font-medium mb-2 px-1">
                      <span>
                        {t('common.coupon_discount') || 'クーポン割引'}
                        {item.coupon_code ? ` (${item.coupon_code.length > 4 ? item.coupon_code.slice(0, -4) : ''}****)` : ''}
                      </span>
                      <span>- {item.coupon_discount.toLocaleString()} 円</span>
                    </div>
                  ) : null}
                </div>
              )}

              {/* Action Buttons for Unpaid/Pending */}
              {/* Fix 9: status=completed + payment_status=unpaid（Stripe離脱後のrefill）も支払いボタンを表示 */}
              {(item.status === 'pending' || item.status === 'unpaid' ||
                (item.status === 'completed' && item.payment_status === 'unpaid')) && !isPurchased && (
                <div className="flex flex-col w-full gap-3 mt-4 pt-4 border-t border-gray-100">
                  {item.id === confirmingRid ? (
                    <button disabled className={classNames(btnLongBase, btnDisabled)}>
                      {t('mypage.confirming_payment', '決済確認中…')}
                    </button>
                  ) : (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setPaymentReservation(item);
                        setShowPaymentDlg(true);
                      }}
                      className={classNames(btnLongBase, btnOrange)}
                    >
                      {t('mypage.pay_now') || '支払う'}
                    </button>
                  )}
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setConfirmConfig({
                        open: true,
                        title: t('common.confirm') || '確認',
                        message: t('mypage.cancel_confirm') || 'キャンセルしますか？',
                        onConfirm: async () => {
                          setConfirmConfig(prev => ({ ...prev, open: false }));
                          try {
                            await axios.delete(`/reservations/${item.id}`);
                            fetchHistory();
                          } catch (e) {
                            alert('キャンセルに失敗しました');
                          }
                        }
                      });
                    }}
                    className="text-[13px] underline decoration-slate-300 underline-offset-4 transition-colors text-center w-full mt-2 text-slate-400 hover:text-red-500"
                  >
                    {t('common.cancel') || 'キャンセル'}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <>
      <div className="flex flex-col flex-1">
        {/* Tabs */}
        <div className="flex gap-6 border-b mb-4">
              <div
                className={`px-1.5 py-2 text-sm md:text-base font-bold border-b-2 cursor-pointer ${activeTab === 'applications' ? 'text-coral border-coral' : 'text-gray-400 border-transparent'
                  }`}
                onClick={() => setActiveTab('applications')}
              >
                {t('mypage.application_history') || '申請履歴'}
              </div>
              <div
                className={`px-1.5 py-2 text-sm md:text-base font-bold border-b-2 cursor-pointer ${activeTab === 'purchases' ? 'text-coral border-coral' : 'text-gray-400 border-transparent'
                  }`}
                onClick={() => setActiveTab('purchases')}
              >
                {t('mypage.purchase_history') || '購入履歴'}
              </div>
            </div>

            {/* Content */}
            <div className="pb-6">
              {loading ? (
                <div className="flex justify-center py-12"><Loader2 className="animate-spin text-coral" /></div>
              ) : (
                <>
                  {activeTab === 'applications' && renderHistoryList(applications, t('mypage.no_applications') || '申請履歴がありません')}
                  {activeTab === 'purchases' && renderHistoryList(purchases, t('mypage.no_history') || '購入履歴がありません')}
                </>
              )}
            </div>

      </div>

      <PaymentConfirmDialog
        open={showPaymentDlg}
        onClose={() => setShowPaymentDlg(false)}
        reservation={paymentReservation as any}
        onConfirm={async () => {
          // Refresh list after payment
          fetchHistory();
          setShowPaymentDlg(false);
        }}
      />

      <UnifiedConfirmDialog
        open={confirmConfig.open}
        onClose={() => setConfirmConfig(prev => ({ ...prev, open: false }))}
        onConfirm={confirmConfig.onConfirm}
        title={confirmConfig.title}
        message={confirmConfig.message}
        type="danger"
      />
      <WeChatGroupFloat />
    </>
  );
};

export default MypageExtra;
